import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react'
import { useLocation } from 'react-router-dom'

import { useModal } from '@context/ModalsContext'
import { SessionExpirationModal } from '@context/modals/SessionExpiration.modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { addEventListeners, removeEventListeners } from '@utils/eventListener.utils'

import { useLogout } from '@hooks/useLogout'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { resetAmplitudeUser } from '@analytics/amplitude'

import { AuthSessionContext } from './AuthContext'

const REDIRECT_TIME = 5 * 60 * 1000 // 5 min
const WARN_AFTER_TIME = 15 * 60 * 1000 // 15 min
const TRADE_WARN_AFTER_TIME = 55 * 60 * 1000 // 55 min (60 min total with redirect time)

interface SessionTimeoutContextValue {
  onStayConnected: () => void
  onLogout: () => void
  timer: number
  redirectAfter: number
  warningModal: boolean
}

export function useSessionTimeout(): SessionTimeoutContextValue {
  const context = useContext(SessionTimeoutContext)
  if (context === undefined) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider')
  }

  return context
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | undefined>(undefined)

export function SessionTimeoutProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [auth] = useContext(AuthSessionContext)
  const { logout } = useLogout()
  const clientApi = useApiClient(ClientApiClient)
  const location = useLocation()
  const isTradeRoute = location.pathname.includes('/dashboard/trade')

  const [lastActivity, setLastActivity] = useLocallyPersistedState<number>(
    'lastActivityTimestamp',
    Date.now()
  )

  const lastActivityUpdateRef = useRef<number>(0)

  const [timer, setTimer] = useLocallyPersistedState<number>('sessionTimer', REDIRECT_TIME)
  const [modalStartTime, setModalStartTime] = useState<number | null>(null)
  const expirationModal = useModal(SessionExpirationModal)

  const resetTimers = useCallback(() => {
    setTimer(REDIRECT_TIME)
    expirationModal.close()
    setLastActivity(Date.now())
    setModalStartTime(null)
  }, [setTimer, expirationModal, setLastActivity])

  const onLogout = useCallback(async () => {
    const currentAuth = auth
    if (currentAuth) {
      await clientApi.logOut(currentAuth.id)
    }
    logout()
    resetAmplitudeUser()

    resetTimers()
  }, [auth, clientApi, logout, resetTimers])

  const onStayConnected = useCallback(() => {
    resetTimers()
  }, [resetTimers])

  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now()
      if (!expirationModal.isOpen && now - lastActivityUpdateRef.current > 30000) {
        setLastActivity(now)
        lastActivityUpdateRef.current = now
      }
    }

    addEventListeners(handleActivity)
    return () => {
      removeEventListeners(handleActivity)
    }
  }, [auth, expirationModal.isOpen, setLastActivity])

  useEffect(() => {
    if (!auth) {
      expirationModal.close()
      return
    }

    const interval = setInterval(() => {
      const now = Date.now()

      if (!expirationModal.isOpen) {
        const inactivity = now - lastActivity
        const warnTime = isTradeRoute ? TRADE_WARN_AFTER_TIME : WARN_AFTER_TIME
        if (inactivity > warnTime) {
          expirationModal.open()
          setModalStartTime(now)
        }
      } else {
        if (modalStartTime !== null) {
          const countdown = REDIRECT_TIME - (now - modalStartTime)
          if (countdown > 0) {
            setTimer(countdown)
          } else {
            onLogout()
            setTimer(0)
          }
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [
    auth,
    expirationModal.isOpen,
    lastActivity,
    modalStartTime,
    setTimer,
    onLogout,
    expirationModal,
    isTradeRoute,
  ])

  const value: SessionTimeoutContextValue = useMemo(
    () => ({
      onStayConnected,
      onLogout,
      timer,
      redirectAfter: REDIRECT_TIME,
      warningModal: expirationModal.isOpen,
    }),
    [onStayConnected, onLogout, timer, expirationModal.isOpen]
  )

  return <SessionTimeoutContext.Provider value={value}>{children}</SessionTimeoutContext.Provider>
}
